// src/layout/components/pipeline/PipelineFunnel.jsx
import React, { useMemo, useState } from "react";
import { THEME, panelStyle } from "../../../ui/theme";
import { Pill } from "../../../ui/primitives";

function fmtInt(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("pt-BR");
}
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

/**
 * Funnel de 6 níveis (contagens independentes).
 * ✅ Card único (mobile-first).
 * ✅ Nunca pressiona laterais (maxWidth/minWidth/boxSizing + truncation).
 * ✅ Clique (l2..l6) chama onJump(levelKey) para navegar até a lista.
 */
export default function PipelineFunnel({
  l1 = 0,
  l2 = 0,
  l3 = 0,
  l4 = 0,
  l5 = 0,
  l6 = 0,
  minVisualPct = 2,
  onJump,
}) {
  const [hoverKey, setHoverKey] = useState(null);

  const items = useMemo(() => {
    const arr = [
      { k: "l1", label: "N1 • Ativados", v: Math.max(0, Number(l1) || 0), alpha: 0.55, tone: "muted" },
      { k: "l2", label: "N2 • Não conectaram", v: Math.max(0, Number(l2) || 0), alpha: 0.55, tone: "warn" },
      { k: "l3", label: "N3 • Ativos ≤ 6m", v: Math.max(0, Number(l3) || 0), alpha: 0.55, tone: "ok" },
      { k: "l4", label: "N4 • Offline > 6m", v: Math.max(0, Number(l4) || 0), alpha: 0.55, tone: "alert" },
      { k: "l5", label: "N5 • Fora do registry", v: Math.max(0, Number(l5) || 0), alpha: 0.55, tone: "alert" },
      { k: "l6", label: "N6 • Sem ativação", v: Math.max(0, Number(l6) || 0), alpha: 0.55, tone: "warn" },
    ];

    const max = Math.max(0, ...arr.map((x) => x.v));
    const minV = Math.max(0, Number(minVisualPct) || 0);

    return arr.map((x) => {
      const rawPct = max > 0 ? (x.v / max) * 100 : 0;
      const w = x.v > 0 ? clamp(rawPct, minV, 100) : 0;
      const relPct = max > 0 ? Math.round((x.v / max) * 100) : 0;
      return { ...x, w, relPct };
    });
  }, [l1, l2, l3, l4, l5, l6, minVisualPct]);

  const clickable = (k) => typeof onJump === "function" && k !== "l1";

  const onKeyActivate = (e, k) => {
    if (!clickable(k)) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onJump(k);
    }
  };

  const fill = (w, alpha) => ({
    position: "absolute",
    inset: 0,
    width: `${w}%`,
    background: `rgba(243,112,33,${alpha})`,
    borderRight: w > 0 ? "1px solid rgba(255,255,255,.10)" : "none",
  });

  const stepStyle = (isClick, isHover) => ({
    position: "relative",
    height: 42,
    borderRadius: 12,
    border: `1px solid ${isHover && isClick ? "rgba(255,255,255,.16)" : "rgba(255,255,255,.10)"}`,
    overflow: "hidden",
    background: isHover && isClick ? "rgba(255,255,255,.05)" : "rgba(255,255,255,.04)",
    transition: "transform 120ms ease, border-color 120ms ease, background 120ms ease",
    transform: isHover && isClick ? "translateY(-1px)" : "translateY(0px)",
    cursor: isClick ? "pointer" : "default",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    WebkitTapHighlightColor: "transparent",
  });

  return (
    <div
      style={{
        ...panelStyle({ padding: 12 }),
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
        <div style={{ minWidth: 0, maxWidth: "100%" }}>
          <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: 0.3 }}>FUNIL</div>
          <div style={{ fontSize: 12, color: THEME.muted, marginTop: 2 }}>
            Clique em um nível para ir à lista.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
          <Pill tone="muted">N1 {fmtInt(l1)}</Pill>
          <Pill tone="warn">N2 {fmtInt(l2)}</Pill>
        </div>
      </div>

      <div style={{ marginTop: 10, display: "grid", gap: 10, minWidth: 0 }}>
        {items.map((it) => {
          const isClick = clickable(it.k);
          const isHover = hoverKey === it.k;

          return (
            <div
              key={it.k}
              style={stepStyle(isClick, isHover)}
              role={isClick ? "button" : undefined}
              tabIndex={isClick ? 0 : -1}
              aria-label={isClick ? `Ir para a lista ${it.label}` : it.label}
              title={isClick ? "Ir para a lista" : undefined}
              onClick={() => (isClick ? onJump(it.k) : null)}
              onKeyDown={(e) => onKeyActivate(e, it.k)}
              onMouseEnter={() => (isClick ? setHoverKey(it.k) : null)}
              onMouseLeave={() => setHoverKey(null)}
            >
              <div style={fill(it.w, it.alpha)} />

              <div
                style={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "0 12px",
                  minWidth: 0,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.12, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      color: THEME.muted,
                      fontWeight: 750,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                    title={it.label}
                  >
                    {it.label}
                  </div>

                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 950, whiteSpace: "nowrap" }}>{fmtInt(it.v)}</div>
                    <div style={{ fontSize: 11, color: THEME.muted, whiteSpace: "nowrap" }}>{it.relPct}%</div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                  {isClick && <div style={{ fontSize: 12, color: THEME.muted, opacity: 0.9 }}>↧</div>}
                  <Pill tone={it.tone}>{it.k.toUpperCase()}</Pill>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
